/**
 * UnitDisclosureEditPage
 * Dynamic form for editing a specific disclosure record.
 * Route: /units/:unitId/disclosures/:disclosureId
 *
 * Uses DisclosureFieldsForm to render fields driven by the disclosure's
 * frontmatter metadata — no hardcoded field definitions per disclosure type.
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  getUnitDisclosure,
  upsertUnitDisclosure,
  markDisclosureReviewed,
  type UnitDisclosureRecord,
} from '../lib/api';
import { DisclosureFieldsForm } from '../components/lease-wizard/DisclosureFieldsForm';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { formatDateTime } from '../utils/date';

// ============================================================================
// HELPERS
// ============================================================================

function formatDisclosureId(id: string): string {
  return id
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}


// ============================================================================
// PAGE COMPONENT
// ============================================================================

export function UnitDisclosureEditPage(): JSX.Element {
  const { unitId, disclosureId } = useParams<{ unitId: string; disclosureId: string }>();
  const navigate = useNavigate();

  const [record, setRecord] = useState<UnitDisclosureRecord | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);

  const [isMarkingReviewed, setIsMarkingReviewed] = useState(false);

  const loadDisclosure = useCallback(async () => {
    if (!unitId || !disclosureId) return;
    try {
      setIsLoading(true);
      setLoadError(null);
      const res = await getUnitDisclosure(unitId, disclosureId);
      setRecord(res.disclosure);
      setFormData((res.disclosure?.data as Record<string, unknown>) ?? {});
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load disclosure');
    } finally {
      setIsLoading(false);
    }
  }, [unitId, disclosureId]);

  useEffect(() => {
    loadDisclosure();
  }, [loadDisclosure]);

  const goBack = useCallback(() => {
    setTimeout(() => navigate(-1), 1000);
  }, [navigate]);

  const handleSave = async (complete: boolean) => {
    if (!unitId || !disclosureId) return;
    try {
      setIsSaving(true);
      setSaveError(null);
      setSaveSuccess(null);
      const res = await upsertUnitDisclosure(unitId, disclosureId, formData, complete);
      setRecord(res.disclosure);
      setSaveSuccess(complete ? 'Disclosure marked as complete.' : 'Draft saved.');
      goBack();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save disclosure');
    } finally {
      setIsSaving(false);
    }
  };

  const handleMarkReviewed = async () => {
    if (!unitId || !disclosureId || !record) return;
    try {
      setIsMarkingReviewed(true);
      setSaveError(null);
      setSaveSuccess(null);
      const res = await markDisclosureReviewed(unitId, disclosureId);
      setRecord(res.disclosure);
      setSaveSuccess('Marked as reviewed.');
      goBack();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to mark as reviewed');
    } finally {
      setIsMarkingReviewed(false);
    }
  };

  if (isLoading) {
    return (
      <div
        className="max-w-4xl mx-auto pb-6 px-[var(--page-padding-x)]"
        data-testid="disclosure-edit-loading"
      >
        <Skeleton className="h-8 w-48 mb-2" />
        <Skeleton className="h-4 w-64 mb-6" />
        <Skeleton className="h-64 w-full rounded-lg" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div
        className="max-w-4xl mx-auto pb-6 px-[var(--page-padding-x)]"
        data-testid="disclosure-edit-error"
      >
        <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
          <p className="text-sm text-destructive m-0">{loadError}</p>
          <Button
            variant="link"
            onClick={loadDisclosure}
            className="mt-2 text-sm text-destructive p-0 h-auto"
            data-testid="disclosure-edit-retry"
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  const disclosureLabel = disclosureId ? formatDisclosureId(disclosureId) : 'Disclosure';

  return (
    <div
      className="max-w-4xl mx-auto pb-6 px-[var(--page-padding-x)]"
      data-testid="disclosure-edit-page"
    >
      {/* Breadcrumb */}
      <div className="flex items-center gap-1 mb-4 text-sm flex-wrap">
        <Link to="/units" className="text-primary hover:text-primary/80 no-underline">
          Units
        </Link>
        <span className="text-muted-foreground">/</span>
        {unitId && (
          <>
            <Link
              to={`/units/${unitId}`}
              className="text-primary hover:text-primary/80 no-underline"
            >
              Unit
            </Link>
            <span className="text-muted-foreground">/</span>
            <Link
              to={`/units/${unitId}/disclosures`}
              className="text-primary hover:text-primary/80 no-underline"
              data-testid="nav-disclosures"
            >
              Disclosures
            </Link>
            <span className="text-muted-foreground">/</span>
          </>
        )}
        <span className="text-muted-foreground">{disclosureLabel}</span>
      </div>

      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-1">
          <h1 className="text-2xl font-semibold text-foreground m-0">
            {disclosureLabel}
          </h1>
          {record && (
            <Badge
              variant={record.status === 'COMPLETE' ? 'default' : 'secondary'}
              aria-label={`Status: ${record.status === 'COMPLETE' ? 'Complete' : 'Draft'}`}
            >
              {record.status === 'COMPLETE' ? 'Complete' : 'Draft'}
            </Badge>
          )}
        </div>
        {record?.lastReviewedAt && (
          <p className="text-sm text-muted-foreground m-0">
            Last reviewed: {record.lastReviewedAt ? formatDateTime(record.lastReviewedAt, { year: 'numeric', month: 'short', day: 'numeric' }) : 'Never'}
          </p>
        )}
        {!record && (
          <p className="text-sm text-muted-foreground m-0">
            No disclosure record exists yet. Fill in the form below to create one.
          </p>
        )}
      </div>

      {/* Form */}
      <form
        data-testid="disclosure-edit-form"
        onSubmit={(e) => {
          e.preventDefault();
          handleSave(false);
        }}
        noValidate
      >
        <div className="bg-card border border-border rounded-lg p-6 mb-6">
          {disclosureId && unitId && (
            <DisclosureFieldsForm
              disclosureId={disclosureId}
              unitId={unitId}
              value={formData}
              onChange={setFormData}
              disabled={isSaving || isMarkingReviewed}
            />
          )}
        </div>

        {/* Error message */}
        {saveError && (
          <div
            className="p-4 mb-4 bg-destructive/10 border border-destructive/20 rounded-lg"
            data-testid="disclosure-save-error"
            role="alert"
          >
            <p className="text-sm text-destructive m-0">{saveError}</p>
          </div>
        )}

        {/* Success message */}
        {saveSuccess && (
          <div
            className="p-4 mb-4 bg-success/10 border border-success/20 rounded-lg"
            data-testid="disclosure-save-success"
            role="status"
            aria-live="polite"
          >
            <p className="text-sm text-success m-0">{saveSuccess}</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            type="submit"
            variant="outline"
            disabled={isSaving}
            className="min-h-[44px]"
            data-testid="disclosure-save-draft"
            aria-disabled={isSaving ? 'true' : undefined}
          >
            {isSaving ? 'Saving...' : 'Save Draft'}
          </Button>

          <Button
            type="button"
            disabled={isSaving}
            onClick={() => handleSave(true)}
            className="min-h-[44px]"
            data-testid="disclosure-save-complete"
            aria-disabled={isSaving ? 'true' : undefined}
          >
            {isSaving ? 'Saving...' : 'Save & Complete'}
          </Button>

          {record && (
            <Button
              type="button"
              variant="outline"
              disabled={isMarkingReviewed}
              onClick={handleMarkReviewed}
              className="min-h-[44px]"
              data-testid="disclosure-mark-reviewed"
              aria-disabled={isMarkingReviewed ? 'true' : undefined}
            >
              {isMarkingReviewed ? 'Marking...' : 'Mark as Reviewed'}
            </Button>
          )}

          <Button
            type="button"
            variant="ghost"
            onClick={() => navigate(-1)}
            className="min-h-[44px]"
            data-testid="disclosure-cancel"
          >
            Back
          </Button>
        </div>
      </form>
    </div>
  );
}
